import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Mountain } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import RegistrationVerification from "./RegistrationVerification";

export default function VerifyEmail() {
  const { home, verifyEmail, user, refreshMe } = useAuth();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [status, setStatus] = useState("checking");
  const [error, setError] = useState("");
  const [verifiedEmail, setVerifiedEmail] = useState("");
  const [pendingSignup, setPendingSignup] = useState(null);
  const changing = params.get("change") === "1";
  const signup = params.get("signup") === "1";
  const signedIn = Boolean(user);
  const verificationRequest = useRef(null);

  useEffect(() => {
    const token = params.get("token");
    if (!token) {
      setStatus("error");
      setError("Verification link is missing a token.");
      return;
    }
    let alive = true;
    if (changing && !signedIn) return;
    const requestKey = `${changing}:${signup}:${token}`;
    if (verificationRequest.current?.key !== requestKey) {
      verificationRequest.current = { key: requestKey, promise: changing
        ? api.post("/account/email/confirm", { token }).then(() => refreshMe())
        : verifyEmail(token, signup) };
    }
    verificationRequest.current.promise
      .then((data) => {
        if (data?.pending_registration || (data?.completed && !data.access_token)) {
          if (alive) setPendingSignup(data);
          return;
        }
        if (alive) setVerifiedEmail(data?.user?.email || "");
        if (signup) sessionStorage.removeItem("trailpulse:pending-registration");
        if (alive) setStatus("verified");
      })
      .catch((err) => {
        if (!alive) return;
        if (signup && err.response?.data?.detail?.code === "registration_already_verified") {
          setVerifiedEmail(err.response.data.detail.email); setStatus("already_verified"); return;
        }
        setStatus("error");
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      });
    return () => { alive = false; };
  }, [params, verifyEmail, changing, signup, signedIn, refreshMe]);

  if (pendingSignup) return <RegistrationVerification registration={pendingSignup} onRestart={() => nav("/register")} />;
  return (
    <div className="tp-app-shell text-[#F4EFEB] antialiased" style={{ fontFamily: "Inter, sans-serif" }}>
      <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6">
        <Link to="/landing" className="mb-8 flex items-center gap-2.5">
          <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#E5B85C]">
            <Mountain className="h-5 w-5 text-[#17211D]" />
          </span>
          <span className="text-lg font-extrabold tracking-tight" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>TrailPulse</span>
        </Link>

        <section className="tp-dark-panel rounded-[2rem] p-8 text-center">
          {changing && user === false ? (
            <><h1 className="text-2xl font-bold">Sign in to update your email</h1><Link className="mt-6 block font-semibold text-[#E5B85C]" to={`/login?next=${encodeURIComponent(`/verify-email?${params.toString()}`)}`}>Sign in</Link></>
          ) : status === "checking" ? (
            <>
              <Loader2 className="mx-auto h-8 w-8 animate-spin text-[#E5B85C]" />
              <h1 className="mt-5 text-2xl font-bold tracking-tight" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>Verifying email</h1>
            </>
          ) : status === "already_verified" ? (
            <><CheckCircle2 className="mx-auto h-10 w-10 text-[#3FA98C]" /><h1 className="mt-5 text-2xl font-bold">Already verified</h1><p className="mt-2 break-all text-sm text-[#E5B85C]">{verifiedEmail}</p><p className="mt-2 text-sm text-[#94A3B8]">This link has already been used. Sign in with this email and your password.</p><Link to="/login" className="mt-6 flex min-h-11 w-full items-center justify-center rounded-full bg-[#E5B85C] py-3 font-bold text-[#17211D]">Sign in</Link></>
          ) : status === "verified" ? (
            <>
              <CheckCircle2 className="mx-auto h-10 w-10 text-[#3FA98C]" />
              <h1 className="mt-5 text-2xl font-bold tracking-tight" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>Email verified</h1>
              {verifiedEmail && <p className="mt-2 break-all font-semibold text-[#E5B85C]">{verifiedEmail}</p>}
              <p className="mt-2 text-sm text-[#94A3B8]">You can now use this email for sign-in and recovery.</p>
              <button type="button" onClick={() => nav(changing ? "/account/settings" : home)} className="mt-6 flex min-h-11 w-full items-center justify-center rounded-full bg-[#E5B85C] py-3 font-bold text-[#17211D]">Continue</button>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-bold tracking-tight text-[#E5695B]" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>Could not verify email</h1>
              <p className="mt-2 text-sm text-[#94A3B8]">{error}</p>
              <Link to={changing ? "/account/settings" : signup ? "/register" : "/verify-contact"} className="mt-6 flex min-h-11 w-full items-center justify-center rounded-full bg-[#E5B85C] py-3 font-bold text-[#17211D]">Back to {changing ? "settings" : signup ? "signup" : "verification"}</Link>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
